import { useMemo } from 'react';

import { ErrorLike, gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { shallowEqual, useSelector } from 'react-redux';

import { selectAccountId } from '@/redux/session/selectors';
import { RootState } from '@/shared/types/state';
import { getAnalyticsRangeLocal } from '@/util/analytics';
import { parseFilter } from '@/util/util';

interface QueryVariables {
  accountId: string;
  startTime: string | null;
  endTime: string | null;
  brainParentIds?: string | null;
  deskIds?: string | null;
  channels?: string | null;
}

interface Rows {
  get_unique_tags: { tags: string[] }[];
}

interface GetUniqueTagsResult {
  isLoading: boolean;
  error?: ErrorLike;
  data?: string[];
}

export const UNIQUE_TAGS = gql`
  query GetUniqueTags(
    $accountId: uuid
    $startTime: timestamp
    $endTime: timestamp
    $brainParentIds: _uuid
    $deskIds: _uuid
    $channels: _text
  ) {
    get_unique_tags(
      args: {
        account_id: $accountId
        start_time: $startTime
        end_time: $endTime
        brain_parent_ids: $brainParentIds
        desk_ids: $deskIds
        channels: $channels
      }
    ) {
      tags
    }
  }
`;

export const useGetUniqueTags = (
  startDate?: string | null,
  endDate?: string | null,
  options?: { skip?: boolean }
): GetUniqueTagsResult => {
  const accountId = useSelector(selectAccountId);
  const { analytics, filtersLoaded } = useSelector(
    (state: RootState) => ({
      analytics: state.analytics.accountAnalytics,
      filtersLoaded: state.analytics.filtersLoaded,
    }),
    shallowEqual
  );

  const { startDate: start, endDate: end } = getAnalyticsRangeLocal(
    startDate || undefined,
    endDate || undefined
  );

  const variables = useMemo(() => {
    const vars: QueryVariables = { startTime: start, endTime: end, accountId } as QueryVariables;

    // Include supported filters from account analytics to scope the tags
    (analytics || []).forEach((filter) => {
      if (filter?.type === 'channels') {
        vars.channels = parseFilter(filter) as string | null;
      } else if (filter?.type === 'deskIds') {
        vars.deskIds = parseFilter(filter) as string | null;
      } else if (filter?.type === 'brainIds') {
        vars.brainParentIds = parseFilter(filter) as string | null;
      }
    });

    return vars;
  }, [accountId, analytics, end, start]);

  const {
    loading,
    data: rawData,
    error,
  } = useQuery<Rows, QueryVariables>(UNIQUE_TAGS, {
    variables,
    skip: options?.skip || !accountId || !startDate || !filtersLoaded,
  });

  const data = rawData?.get_unique_tags?.[0]?.tags;

  if (loading || error) {
    return { isLoading: loading, error };
  }

  return { isLoading: loading, data };
};
